import { defineStore } from 'pinia'
import { useUserStore } from '@/authentication/user.store'
import { useContentProfileStore } from '@/contentprofile/contentprofile.store'
import { domainToProfileIdOptionsMap } from '@/contentprofile/domain_content_profiles'

export const GENERATE_ASSESSMENT_FEATURE = 'toolkit-pma-generation'
const TRUTHY_FLAG_VALUES = ['true', '1', 'yes']
const TEKS_USER_TYPE = 'teks'
const TEXAS_CONTENT_PROFILE_ID = 'texas'
const DISABLED_THREADS_DOMAIN_NAME_PATTERNS = []
const DISABLED_REPORT_UPLOAD_DOMAIN_NAME_PATTERNS = ['(NYC', 'Mott Haven Academy Charter School (NY)', 'New York City Geographic District #20', 'St. Dominic TORCH (NY)', 'United Charter High Schools (NY)']
// Per-content-profile Present Levels Wizard subject suppression, requested directly by each district.
const HIDDEN_PRESENT_LEVELS_SUBJECTS_BY_PROFILE = {
  'nevada-no-iod': ['Behavior'],
}
const CCSD_PROFILE_ID = 'nevada-no-iod'
const GROW_ACCESS = { domainFlag: 'grow_access', userTag: 'grow access' }
const HB2_ACCESS = { domainFlag: 'tx_hb2_access', userTag: 'tx hb2 access' }
// Shared verbatim with threads-app, which gates its Transition Plan route on the same exclusion.
const TRANSITION_THREAD_FEATURE = 'feature-threads-transition'
// Shared verbatim with threads-app, which hides BSP on the same exclusion.
const BSP_THREAD_FEATURE = 'feature-threads-bsp'

function userTags(userStore) {
  return (userStore.user.tags || []).map((tag) => String(tag).toLowerCase())
}

function hasAccess(userStore, { domainFlag, userTag }) {
  const hasDomainFlag = domainFlagNames(userStore.domainInfo.flags).includes(domainFlag)
  return hasDomainFlag || userTags(userStore).includes(userTag)
}

function isTruthyFlagValue(value) {
  if (typeof value === 'string') return TRUTHY_FLAG_VALUES.includes(value.toLowerCase())
  return value === true || value === 1
}

// Domain flags arrive as a list from the account graph but as a {flag: value} map from some
// imports, so both shapes are read rather than throwing on the map.
function domainFlagNames(flags) {
  if (Array.isArray(flags)) return flags.map((flag) => String(flag).toLowerCase())
  if (flags && typeof flags === 'object') {
    return Object.keys(flags).filter((key) => isTruthyFlagValue(flags[key])).map((key) => key.toLowerCase())
  }
  return []
}

// Every domain, not just the first: an opt-out checked on one domain would show the feature to a
// district that excluded itself whenever the graph returned another of the user's domains first.
function anyDomainHasFlag(domains, flagName) {
  return (domains || []).some((domain) => domainFlagNames(domain && domain.flags).includes(flagName))
}

export const useVisibilityStore = defineStore('visibility', {
  state: () => ({
    betaMode: false,
    editorMode: false,
    iodMode: null,
    iodGuardrailMode: null,
    effectsMode: null,
    shareMode: null,
    threadsMode: null,
    generateAssessmentMode: null,
    orientationMobilityMode: false,
    reportUploadMode: null,
    analyticsDashboardMode: null,
    disabledThreadsDomains: DISABLED_THREADS_DOMAIN_NAME_PATTERNS,
    disabledReportUploadDomains: DISABLED_REPORT_UPLOAD_DOMAIN_NAME_PATTERNS,
  }),

  getters: {
    editorVisibility(state) {
      const userStore = useUserStore()
      return state.editorMode && userStore.isSuperAdmin
    },
    betaVisibility(state) {
      const userStore = useUserStore()
      return state.betaMode && userStore.isAuthenticated
    },
    teksVisibility() {
      const userStore = useUserStore()
      const contentProfile = useContentProfileStore()
      const isTeksUser = userStore.user_type === TEKS_USER_TYPE || userStore.userContentProfileId === TEXAS_CONTENT_PROFILE_ID
      return isTeksUser || contentProfile.profileId === TEXAS_CONTENT_PROFILE_ID
    },
    selVisibility() {
      const contentProfile = useContentProfileStore()
      const profileId = contentProfile.profileId ||  ''
      return !profileId.endsWith('_no_SEL')
    },
    iodVisibility(state) {
      if (state.iodMode !== null) return state.iodMode
      const contentProfile = useContentProfileStore()
      // check if the content profile is one that doesn't allow IOD feature
      const noIODProfiles = ['nevada-no-iod', 'no-iod-test']
      return noIODProfiles.indexOf(contentProfile.profileId) === -1
    },
    iodGuardrailVisibility(state) {
      if (state.iodGuardrailMode !== null) return state.iodGuardrailMode
      const contentProfile = useContentProfileStore()
      // The IOD guardrail is scoped to the base 'texas' profile only.
      // Other Texas variants (texas_no_SEL, texas_dallas) are intentionally excluded.
      return contentProfile.profileId === 'texas'
    },
    analyticsDashboardVisibility(state) {
      if (state.analyticsDashboardMode !== null) return state.analyticsDashboardMode
      const userStore = useUserStore()
      const domainFlags = userStore.domainInfo?.flags || []
      return domainFlags.includes('analytics_dashboard')
    },
    effectsVisibility(state) {
      if (state.effectsMode !== null) return state.effectsMode
      // check if the content profile is one that has effect statements in PLW
      const contentProfile = useContentProfileStore()
      const effectsProfiles = ['nevada-no-iod']
      return effectsProfiles.indexOf(contentProfile.profileId) > -1
    },
    hiddenPresentLevelsSubjectLabels() {
      const contentProfile = useContentProfileStore()
      return HIDDEN_PRESENT_LEVELS_SUBJECTS_BY_PROFILE[contentProfile.profileId] || []
    },
    shareVisibility(state) {
      if (state.shareMode !== null) return state.shareMode
      const contentProfile = useContentProfileStore()
      // check if the content profile is one that doesn't allow share feature
      const noShareProfiles = ['florida_no_share_no_SEL']
      return noShareProfiles.indexOf(contentProfile.profileId) === -1
    },
    threadsVisibility(state) {
      if (state.threadsMode !== null) return state.threadsMode
      const userStore = useUserStore()
      const contentProfile = useContentProfileStore()
      const isAuthenticated = userStore.isAuthenticated
      if (!isAuthenticated) return false

      const userDomainName = userStore.domainInfo?.name || ''
      const belongsToDisabledDomain = state.disabledThreadsDomains.some(domain => userDomainName.includes(domain))

      const hasNoThreadsFlag = domainFlagNames(userStore.domainInfo?.flags).includes('no_threads')

      const profileId = contentProfile.profileId || ''
      const hasNoAIProfile = profileId.endsWith('no-ai')

      const hasAccessOverride = userStore.hasThreadsAccessOverrideTag
      const blockedByDomain = (belongsToDisabledDomain || hasNoThreadsFlag) && !hasAccessOverride

      return !blockedByDomain && !hasNoAIProfile
    },
    reportUploadVisibility(state) {
      if (state.reportUploadMode !== null) return state.reportUploadMode
      const userStore = useUserStore()
      const isAuthenticated = userStore.isAuthenticated
      if (!isAuthenticated) return false

      const userDomainName = userStore.domainInfo?.name || ''
      const belongsToDisabledDomain = state.disabledReportUploadDomains.some(domain => userDomainName.includes(domain))

      return !belongsToDisabledDomain
    },
    // The bare <feature> user tag opts in and beats exclude-<feature> user tag, which beats
    // exclude_<feature> domain flag; tags are kebab-case and domain flags snake_case, matching Threads.
    /** @returns {(featureKey: string) => boolean} */
    isFeatureExcluded() {
      const userStore = useUserStore()
      const tags = (userStore.user.tags || []).map((tag) => String(tag).toLowerCase())
      return (featureKey) => {
        if (tags.includes(featureKey)) return false
        if (tags.includes(`exclude-${featureKey}`)) return true
        return anyDomainHasFlag(userStore.domains, `exclude_${featureKey.replace(/-/g, '_')}`)
      }
    },
    // Generated assessments are made by AI, so the no-ai floor is the only gate below the
    // feature's own opt-out. Not threadsVisibility: a no_threads district still gets this feature,
    // matching toolkit-spa.
    /** @returns {boolean} */
    generateAssessmentVisibility(state) {
      if (state.generateAssessmentMode !== null) return state.generateAssessmentMode
      if (!useUserStore().isAuthenticated || !this.aiFeatureVisibility) return false
      return !this.isFeatureExcluded(GENERATE_ASSESSMENT_FEATURE)
    },
    orientationMobilityVisibility(state) {
      return state.orientationMobilityMode
    },
    canChangeProfile() {
      const userStore = useUserStore()
      return userStore.domains.some((domain) => new Set(Object.keys(domainToProfileIdOptionsMap)).has(domain.uuid))
    },
    aiFeatureVisibility() {
      const contentProfile = useContentProfileStore()
      const profileId = contentProfile.profileId || ''
      return !profileId.endsWith('no-ai')
    },
    nycWebinarsVisibility() {
      const contentProfile = useContentProfileStore()
      return contentProfile.profileId === 'new-york_no-ai'
    },
    isCcsdProfile() {
      return useContentProfileStore().profileId === CCSD_PROFILE_ID
    },
    transitionThreadVisibility() {
      return !this.isFeatureExcluded(TRANSITION_THREAD_FEATURE)
    },
    bspThreadVisibility() {
      return !this.isFeatureExcluded(BSP_THREAD_FEATURE)
    },
    growVisibility() {
      return hasAccess(useUserStore(), GROW_ACCESS)
    },
    hb2Visibility() {
      return hasAccess(useUserStore(), HB2_ACCESS)
    },
  },

  actions: {
    toggleGenerateAssessmentMode(flag) {
      this.generateAssessmentMode = flag
    },
    toggleBetaMode(flag) {
      this.betaMode = flag
    },
    toggleEditorMode(flag) {
      this.editorMode = flag
    },
    toggleIodMode(flag) {
      this.iodMode = flag
    },
    toggleIodGuardrailMode(flag) {
      this.iodGuardrailMode = flag
    },
    toggleEffectsMode(flag) {
      this.effectsMode = flag
    },
    toggleShareMode(flag) {
      this.shareMode = flag
    },
    toggleThreadsMode(flag) {
      this.threadsMode = flag
    },
    toggleOrientationMobilityMode(flag) {
      this.orientationMobilityMode = flag
    },
  }
})
